import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  MobileNoValidator,
  pincodeWOValueValidator,
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import {
  createBranch,
  getBranchByID,
  updateBranch,
} from "./services/branch.services";
import { clearBranchDetails } from "./branchSlice";
import { getAllActiveCities } from "../city/services/city.services";
import { getAllActiveStates } from "../state/services/state.services";
import { getAllActiveCountries } from "../country/services/country.services";
import { ICity } from "../city/cityModel";
import { findIndex } from "lodash";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { getAllActiveCompanies } from "../company/services/company.services";
import { ICompany } from "../company/companyModel";
import { IState } from "../state/stateModel";
import { ICountry } from "../country/countryModel";
import { STATUSARRAY } from "../../_contstants/common";

interface CityChangeWatcherProps {
  formRenderProps: FormRenderProps;
  CityList: ICity[];
}

const CityChangeWatcher: React.FC<CityChangeWatcherProps> = ({
  CityList,
  formRenderProps,
}) => {
  const cityID = formRenderProps.valueGetter("CityID");

  useEffect(() => {
    const CityID = cityID ? cityID : undefined;
    const cityIndex = findIndex(CityList, { ID: CityID });
    if (cityIndex > -1) {
      const city = CityList[cityIndex];
      formRenderProps.onChange("StateID", { value: city.StateID });
      formRenderProps.onChange("CountryID", { value: city.CountryID });
    } else {
      formRenderProps.onChange("CityID", { value: "" });
      formRenderProps.onChange("StateID", { value: "" });
      formRenderProps.onChange("CountryID", { value: "" });
    }
  }, [cityID]);
  return null;
};

const CreateBranch: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const BranchID = location.state?.BranchID;

  const BranchDetail = useAppSelector((state) => state.branch.BranchDetail);
  const loading = useAppSelector((state) => state.branch.loading);
  const CityList = useAppSelector((state) => state.city.CityList);
  const StateList = useAppSelector((state) => state.state.StateList);
  const CountryList = useAppSelector((state) => state.country.CountryList);
  const CompanyList = useAppSelector((state) => state.company.CompanyList);
  const [formKey, setFormKey] = React.useState(1);

  useEffect(() => {
    if (BranchID) {
      dispatch(getBranchByID(BranchID));
    }
    return () => {
      dispatch(clearBranchDetails());
    };
  }, [BranchID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [BranchDetail, CityList]);

  useEffect(() => {
    dispatch(getAllActiveCities());
    dispatch(getAllActiveStates());
    dispatch(getAllActiveCountries());
    dispatch(getAllActiveCompanies());
  }, []);

  const handleSubmit = async (values: any) => {
    try {
      if (BranchID) {
        const editPayload = {
          ID: BranchID,
          CompanyID: values.CompanyID ? +values.CompanyID : null,
          BranchName: values.BranchName ? values.BranchName : "",
          Address: values.Address ? values.Address : "",
          CityID: values.CityID ? +values.CityID : null,
          StateID: values.StateID ? +values.StateID : null,
          CountryID: values.CountryID ? +values.CountryID : null,
          Pincode: values.Pincode ? values.Pincode.toString() : "",
          BranchHead: values.BranchHead ? values.BranchHead : "",
          MobileNo: values.MobileNo ? values.MobileNo.toString() : "",
          EmailID: values.EmailID ? values.EmailID : "",
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateBranch(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate(-1);
        }
      } else {
        const insertPayload = {
          CompanyID: values.CompanyID ? +values.CompanyID : null,
          BranchName: values.BranchName ? values.BranchName : "",
          Address: values.Address ? values.Address : "",
          CityID: values.CityID ? +values.CityID : null,
          StateID: values.StateID ? +values.StateID : null,
          CountryID: values.CountryID ? +values.CountryID : null,
          Pincode: values.Pincode ? values.Pincode.toString() : "",
          BranchHead: values.BranchHead ? values.BranchHead : "",
          MobileNo: values.MobileNo ? values.MobileNo.toString() : "",
          EmailID: values.EmailID ? values.EmailID : "",
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createBranch(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate("/branch");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={BranchDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 5, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h4>
                    {BranchID ? "Update Branch" : "Add Branch"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="BranchName"
                    label="Branch"
                    placeholder="Branch"
                    component={FormTextField}
                    astrike={true}
                    validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"CompanyID"}
                    name={"CompanyID"}
                    label={"Company"}
                    placeholder="Company"
                    astrike={true}
                    component={FormSelectionField}
                    validator={requiredValidator}
                    options={CompanyList?.map((company: ICompany) => {
                      return {
                        value: company?.ID,
                        label: company?.CompanyName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="BranchHead"
                    astrike={true}
                    label="Branch Head"
                    placeholder="Branch Head"
                    component={FormTextField}
                    validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="Address"
                    label="Address"
                    placeholder="Address"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="Pincode"
                    label="Pincode"
                    type="number"
                    placeholder="Pincode"
                    max={6}
                    minNumber="0"
                    component={FormTextField}
                    validator={pincodeWOValueValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"CityID"}
                    name={"CityID"}
                    astrike={true}
                    label={"City"}
                    placeholder="i.e. Ahmedabad"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    options={CityList?.map((city: ICity) => {
                      return {
                        value: city?.ID,
                        label: city?.CityName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"StateID"}
                    name={"StateID"}
                    label={"State"}
                    placeholder="i.e. Gujarat"
                    component={FormSelectionField}
                    // disabled={true}
                    options={StateList?.map((state: IState) => {
                      return {
                        value: state?.ID,
                        label: state?.StateName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"CountryID"}
                    name={"CountryID"}
                    label={"Country"}
                    component={FormSelectionField}
                    placeholder="i.e. India"
                    // disabled={true}
                    options={CountryList?.map((country: ICountry) => {
                      return {
                        value: country?.ID,
                        label: country?.CountryName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="MobileNo"
                    label="Mobile No"
                    type="number"
                    max={10}
                    placeholder="i.e. 9999999999"
                    component={FormTextField}
                    validator={MobileNoValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="EmailID"
                    label="Email ID"
                    astrike={true}
                    placeholder="i.e. example@gmail.com"
                    component={FormTextField}
                    validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name={`IsActive`}
                    label="Status"
                    placeholder="i.e. Active"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  colSpan={3}
                  style={{
                    marginTop: "20px",
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "end",
                  }}
                >
                  <div>
                    <ButtonWithLoading
                      label={BranchID ? "Update" : "Save"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />

                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => navigate("/branch")}
                    >
                      Cancel
                    </Button>
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
            <CityChangeWatcher
              formRenderProps={formRenderProps}
              CityList={CityList}
            />
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateBranch;
